import { memo, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import {
  ERROR_NOT_OPTIMISED_TITLE,
  ERROR_NOT_OPTIMISED,
} from "../../consts/strings";
import { View } from "../Base";
import { Modal } from "../Modals/Modal";
import {
  nonRedirectionRoutesForPlanTypeSelector,
  volumeDataSelector,
  depotRoutesOptimisedSelector,
  isEditOneModeSelector,
} from "../../reducers/matrixProfile/selectors";
import { hasFiltersEnabledSelector } from "../../reducers/filters/selectors";
import { resetFilter } from "../../reducers/filters";
import { getRoutesExtract } from "../../utils/routeExtract";
import { palette } from "../../theme/palette";

const useStyles = makeStyles({
  container: {
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
    height: "30px",
  },
  button: {
    backgroundColor: palette.sunburst,
    fontSize: "11px",
    height: "17px",
    width: "95px",
    "&:hover": {
      backgroundColor: palette.sunburst,
      opacity: 0.6,
    },
  },
  buttonDisabled: {
    backgroundColor: palette.typography.white.disabled,
  },
  text: {
    color: palette.white,
    fontSize: "14px",
    fontWeight: "500",
  },
  anchor: {
    display: "none",
  },
  modalContentContainer: {
    width: "500px",
  },
  modalButtonContainer: {
    width: "100%",
    display: "flex",
    justifyContent: "flex-end",
    padding: "16px",
  },
  modalButtonBlue: {
    background: palette.btn.blueFill,
    color: palette.typography.white.high,
    border: palette.btn.blueBorder,
    boxShadow: "none",
    "&:hover": {
      background: palette.btn.blueFill,
    },
  },
});

export const RouteExtractBar = memo(({ routes, drivers }) => {
  const styles = useStyles();
  const downloadRef = useRef();
  const dispatch = useDispatch();
  const isEditOneMode = useSelector(isEditOneModeSelector);
  const hasFiltersEnabled = useSelector(hasFiltersEnabledSelector);
  const totalRoutes =
    useSelector(nonRedirectionRoutesForPlanTypeSelector)?.length || 0;
  const hasVolumeData = !!useSelector(volumeDataSelector);
  const isOptimised = !!useSelector(depotRoutesOptimisedSelector);
  const [{ url, fileName }, setDownload] = useState({});
  const [notOptimisedDialogOpen, setNotOptimisedDialogOpen] = useState(false);
  const disableClearFilters = !hasFiltersEnabled || isEditOneMode;

  useEffect(() => {
    if (!url || !downloadRef?.current) return;
    downloadRef.current.click();
    URL.revokeObjectURL(url);
    setDownload({});
  }, [url]);

  const onExtractClick = () => {
    if (url) return;
    if (hasVolumeData && !isOptimised) setNotOptimisedDialogOpen(true);
    else setDownload(getRoutesExtract(routes, drivers, hasVolumeData));
  };

  return totalRoutes ? (
    <View className={styles.container}>
      <Typography className={styles.text}>
        {`Routes ${routes.length}/${totalRoutes}`}
      </Typography>
      <Button className={styles.button} onClick={onExtractClick}>
        Extract CSV
      </Button>
      <Button
        className={clsx(
          styles.button,
          disableClearFilters && styles.buttonDisabled
        )}
        onClick={() => dispatch(resetFilter())}
        disabled={disableClearFilters}
      >
        Clear Filters
      </Button>
      <a
        className={styles.anchor}
        ref={downloadRef}
        download={fileName}
        href={url}
      >
        Download Route Extract
      </a>
      <Modal
        open={notOptimisedDialogOpen}
        onClose={() => setNotOptimisedDialogOpen(false)}
        title={ERROR_NOT_OPTIMISED_TITLE}
        content={
          <View className={styles.modalContentContainer}>
            {ERROR_NOT_OPTIMISED}
          </View>
        }
        actions={
          <View className={styles.modalButtonContainer}>
            <Button
              className={styles.modalButtonBlue}
              variant={"contained"}
              onClick={() => setNotOptimisedDialogOpen(false)}
            >
              Ok
            </Button>
          </View>
        }
      />
    </View>
  ) : null;
});
